<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">
      Manage Pre-Start Check Lists
    </h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :list-options="list_options"
      :new-to="{ name: 'pre_start_check_list.create', params: { id: 0 } }"
      :query-select-options="options"
      :schema="schema"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      new-button-text="New Pre-Start Check List"
      @filter_changed="update_filter($event)"
      @options_changed="update_table($event)"
      @reset_table="reset_table"
    >
      <template #middle>
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          enable-delete
          enable-merge
          @delete="doDeleteItems"
          @merge="doMergeItems"
        />
      </template>
    </o-list-toolbar>

    <BTable
      :key="selected.selectable"
      :busy="working"
      :fields="fields"
      :items="items"
      :selectable="selected.selectable"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
      bordered
      class="o-list"
      head-variant="light"
      no-local-sorting
      striped
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      @sort-changed="change_sort"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template v-if="filterRow" #top-row="{ columns, fields }">
        <filter-cell
          v-for="field in fields"
          :key="field.key"
          :field="field"
          :options="filter_row_options"
          :qbe="list_options.qbe"
          :schema="schemaModel"
          @filter="filter_changed"
        />
      </template>
      <template #cell(active_end_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_end_date'"
        >
          <date-display
            :value="item.active_end_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(active_start_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'active_start_date'"
        >
          <date-display
            :value="item.active_start_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'comments'"
        >
          <default-display
            :value="item.comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(created_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(driver)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'driver'"
        >
          <lookup-display
            :list="optionCodeTruckItemsById"
            :value="item.driver"
            default-value="-"
            display-field="name"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template #cell(general_visual_comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'general_visual_comments'"
        >
          <default-display
            :value="item.general_visual_comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(general_visual_inspection)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'general_visual_inspection'"
        >
          <boolean-display
            :value="item.general_visual_inspection"
          ></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(head_lights_comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'head_lights_comments'"
        >
          <default-display
            :value="item.head_lights_comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(head_lights_inspection)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'head_lights_inspection'"
        >
          <boolean-display
            :value="item.head_lights_inspection"
          ></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(modified_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'modified_date'"
        >
          <date-display
            :value="item.modified_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(odometer)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'odometer'"
        >
          <number-display :decimals="0" :value="item.odometer"/>
        </o-link-cell>
      </template>
      <template #cell(side_lights_comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'side_lights_comments'"
        >
          <default-display
            :value="item.side_lights_comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(side_lights_inspection)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'side_lights_inspection'"
        >
          <boolean-display
            :value="item.side_lights_inspection"
          ></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(tail_lights_comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'tail_lights_comments'"
        >
          <default-display
            :value="item.tail_lights_comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(tail_lights_inspection)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'tail_lights_inspection'"
        >
          <boolean-display
            :value="item.tail_lights_inspection"
          ></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(tyres_comments)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'tyres_comments'"
        >
          <default-display
            :value="item.tyres_comments"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(tyres_inspection)="{ index, item }">
        <o-link-cell
          :to="{ name: 'pre_start_check_list.edit', params: { id: item.id } }"
          :when="first_column_key === 'tyres_inspection'"
        >
          <boolean-display :value="item.tyres_inspection"></boolean-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'pre_start_check_list.create' }"
        >create a new item
        </router-link
        >
      </template>
    </BTable>

    <o-list-paging
      :limit="list_options.limit"
      :offset="list_options.offset"
      :page-options="rows_per_page_items"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {BTable} from "bootstrap-vue";
import {CreateGroupPermissionMixin, CreateListPageMixin, MultiActionsMixin,} from "@/vue-ocom/mixins";
import FetchAndUseCodeModMixin from "@/vue-ocom/mixins/FetchAndUseCodeModMixin";
import {fieldToTableColumn, GetSearchFields,} from "@/vue-ocom/schema/schema_utils";
import {cleanUpModel} from "@/vue-ocom/schema/schema_vuetify.js";
import schema from "../../schema/index.js";

import OListToolbar from "@/vue-ocom/components/list/OListToolbar.vue";
import OListPaging from "@/vue-ocom/components/list/OListPaging.vue";
import DefaultDisplay from "@/vue-ocom/components/display/DefaultDisplay.vue";
import DateDisplay from "@/vue-ocom/components/display/DateDisplay.vue";
import NumberDisplay from "@/vue-ocom/components/display/NumberDisplay.vue";
import BooleanDisplay from "@/vue-ocom/components/display/BooleanDisplay.vue";
import LookupDisplay from "@/vue-ocom/components/display/LookupDisplay.vue";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad.vue";
import EmailIcon from "vue-material-design-icons/Email.vue";
import LinkIcon from "vue-material-design-icons/Link.vue";
import FilterCell from "@/vue-ocom/components/list/filter/FilterCell.vue";

export default {
  components: {
    BTable,
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
    FilterCell,
  },
  mixins: [
    CreateListPageMixin("PreStartCheckList", "pre_start_check_list"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("PreStartCheckList"),
    FetchAndUseCodeModMixin(["CodeTruck"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.PreStartCheckList,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.PreStartCheckList),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        {name: "Active", code: "active", options: {offset: 0, sort: "id"}},
        {
          name: "Non Active",
          code: "inactive",
          options: {offset: 0, sort: "id"},
        },
        {name: "All", code: "all", options: {offset: 0, sort: "id"}},
      ],

      user_column_selection:
        null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        CodeTruck: {
          options: this.optionCodeTruckItems,
          textField: "name",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "odometer",
        "comments",
        "driver",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    filter_row_options() {
      // Options for the FilterRow components
      return {
        id: false /*No filter for this field */,
        odometer: {},
        general_visual_inspection: {},
        general_visual_comments: {},
        tyres_inspection: {},
        tyres_comments: {},
        head_lights_inspection: {},
        head_lights_comments: {},
        tail_lights_inspection: {},
        tail_lights_comments: {},
        side_lights_inspection: {},
        side_lights_comments: {},
        comments: {},
        driver: {
          options: this.optionCodeTruckItems,
          loading: this.optionCodeTruckWorking,
          "text-field": "name",
          "value-field": "id",
          "sort-field": "name",
          "blank-option-text": "",
        },
        active_start_date: {},
        active_end_date: {},
        modified_date: {},
        created_date: {},
      };
    },
  },
};
</script>
